import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Plus, 
  Filter, 
  Search, 
  BarChart3, 
  TrendingUp, 
  Target,
  DollarSign,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  MoreHorizontal,
  Calendar,
  CheckCircle,
  Clock,
  Play,
  Pause,
  Square
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { SocialCampaign, Platform } from '@/types/social'
import { formatDistanceToNow, format } from 'date-fns'

// Mock campaign data
const mockCampaigns: SocialCampaign[] = [
  {
    id: '1',
    name: 'Winter Home Maintenance Campaign',
    description: 'Educational campaign about winter home preparation and masonry protection',
    campaign_type: 'awareness',
    status: 'active',
    start_date: '2025-01-01',
    end_date: '2025-02-28',
    budget: 2500,
    target_audience: {
      demographics: 'Homeowners 35-65',
      interests: ['Home improvement', 'Property maintenance'],
      locations: ['Toronto', 'Hamilton', 'North York']
    },
    platforms: ['INSTAGRAM', 'FACEBOOK', 'LINKEDIN'],
    hashtags: ['#WinterMaintenance', '#HomeCare', '#MasonryTips'],
    goals: [
      {
        metric: 'reach',
        target_value: 50000,
        current_value: 32000
      },
      {
        metric: 'engagement',
        target_value: 2500,
        current_value: 1850
      },
      {
        metric: 'leads',
        target_value: 25,
        current_value: 18
      }
    ],
    post_ids: ['1', '2', '3'],
    performance_metrics: {
      total_reach: 32000,
      total_engagement: 1850,
      total_clicks: 420,
      conversion_rate: 4.3,
      roi: 180
    },
    created_at: '2024-12-15T00:00:00Z',
    updated_at: '2025-01-10T12:00:00Z'
  },
  {
    id: '2',
    name: 'Spring Restoration Services',
    description: 'Promotional campaign for spring masonry and restoration services',
    campaign_type: 'sales',
    status: 'draft',
    start_date: '2025-03-01',
    end_date: '2025-05-31',
    budget: 4000,
    target_audience: {
      demographics: 'Property owners 40+',
      interests: ['Heritage homes', 'Property restoration'],
      locations: ['Toronto', 'Hamilton']
    },
    platforms: ['INSTAGRAM', 'FACEBOOK', 'TWITTER'],
    hashtags: ['#SpringRestoration', '#HeritageHomes', '#MasonryExperts'],
    goals: [
      {
        metric: 'leads',
        target_value: 50,
        current_value: 0
      },
      {
        metric: 'conversions',
        target_value: 15,
        current_value: 0
      },
      {
        metric: 'revenue',
        target_value: 75000,
        current_value: 0
      }
    ],
    post_ids: [],
    performance_metrics: {
      total_reach: 0,
      total_engagement: 0,
      total_clicks: 0,
      conversion_rate: 0,
      roi: 0
    },
    created_at: '2025-01-05T00:00:00Z',
    updated_at: '2025-01-05T00:00:00Z'
  },
  {
    id: '3',
    name: 'Educational Masonry Series',
    description: 'Long-form educational content about masonry techniques and best practices',
    campaign_type: 'engagement',
    status: 'completed',
    start_date: '2024-09-01',
    end_date: '2024-12-31',
    budget: 1500,
    target_audience: {
      demographics: 'DIY enthusiasts, contractors',
      interests: ['Construction', 'DIY', 'Professional development'],
      locations: ['Ontario']
    },
    platforms: ['LINKEDIN', 'INSTAGRAM', 'FACEBOOK'],
    hashtags: ['#MasonryEducation', '#DIYTips', '#ProfessionalMasonry'],
    goals: [
      {
        metric: 'engagement',
        target_value: 5000,
        current_value: 6200
      },
      {
        metric: 'followers',
        target_value: 500,
        current_value: 650
      },
      {
        metric: 'shares',
        target_value: 200,
        current_value: 280
      }
    ],
    post_ids: ['4', '5', '6', '7', '8'],
    performance_metrics: {
      total_reach: 85000,
      total_engagement: 6200,
      total_clicks: 1250,
      conversion_rate: 12.8,
      roi: 320
    },
    created_at: '2024-08-15T00:00:00Z',
    updated_at: '2024-12-31T23:59:59Z'
  }
]

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

const STATUS_ICONS = {
  draft: Clock,
  active: Play,
  paused: Pause,
  completed: CheckCircle
}

const STATUS_COLORS = {
  draft: 'text-muted-foreground',
  active: 'text-green-600',
  paused: 'text-orange-600',
  completed: 'text-blue-600'
}

export default function CampaignTracking() {
  const [selectedStatus, setSelectedStatus] = useState<SocialCampaign['status'] | 'all'>('all')
  const [selectedType, setSelectedType] = useState<SocialCampaign['campaign_type'] | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // Mock data query
  const { data: campaigns = mockCampaigns } = useQuery({
    queryKey: ['campaigns', selectedStatus, selectedType, searchQuery],
    queryFn: () => {
      let filtered = mockCampaigns

      if (selectedStatus !== 'all') {
        filtered = filtered.filter(campaign => campaign.status === selectedStatus)
      }

      if (selectedType !== 'all') {
        filtered = filtered.filter(campaign => campaign.campaign_type === selectedType)
      }

      if (searchQuery) {
        filtered = filtered.filter(campaign =>
          campaign.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          campaign.description?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      }

      return Promise.resolve(filtered)
    }
  })

  const getOverallStats = () => {
    const activeCampaigns = campaigns.filter(c => c.status === 'active').length
    const totalBudget = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0)
    const totalReach = campaigns.reduce((sum, c) => sum + (c.performance_metrics?.total_reach || 0), 0)
    const totalEngagement = campaigns.reduce((sum, c) => sum + (c.performance_metrics?.total_engagement || 0), 0)
    const avgROI = campaigns.length > 0
      ? campaigns.reduce((sum, c) => sum + (c.performance_metrics?.roi || 0), 0) / campaigns.length
      : 0

    return {
      totalCampaigns: campaigns.length,
      activeCampaigns,
      totalBudget,
      totalReach,
      totalEngagement,
      avgROI
    }
  }

  const overallStats = getOverallStats()

  const getGoalProgress = (goal: SocialCampaign['goals'][0]) => {
    const progress = goal.target_value > 0 ? ((goal.current_value || 0) / goal.target_value) * 100 : 0
    return Math.min(progress, 100)
  }

  const formatMetric = (value: number, metric: string) => {
    switch (metric) {
      case 'reach':
      case 'impressions':
      case 'followers':
        return value.toLocaleString()
      case 'engagement':
      case 'clicks':
      case 'leads':
      case 'conversions':
        return value.toString()
      case 'revenue':
        return `$${value.toLocaleString()}`
      case 'roi':
        return `${value}%`
      case 'conversion_rate':
        return `${value.toFixed(1)}%`
      default:
        return value.toString()
    }
  }

  const handleCampaignAction = (campaignId: string, action: 'play' | 'pause' | 'stop' | 'edit' | 'delete') => {
    console.log(`Campaign ${campaignId} action: ${action}`)
    // Implement action logic here
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Campaign Tracking</h1>
          <p className="text-muted-foreground">
            Monitor and analyze your social media campaigns' performance and ROI
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics Report
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <div className="p-6">
                <h2 className="text-2xl font-bold mb-4">Create New Campaign</h2>
                <p className="text-muted-foreground">
                  Campaign creation form would go here...
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Campaigns</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{overallStats.totalCampaigns}</div>
            <p className="text-xs text-muted-foreground">
              {overallStats.activeCampaigns} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Budget</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${overallStats.totalBudget.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Across all campaigns
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overallStats.totalReach.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              People reached
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overallStats.totalEngagement.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              Total interactions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average ROI</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overallStats.avgROI.toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground">
              Return on investment
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">Excellent</div>
            <p className="text-xs text-muted-foreground">
              Overall rating
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Campaign Management</CardTitle>
              <CardDescription>
                Track, analyze, and optimize your social media campaigns
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="all" className="space-y-4">
            <div className="flex items-center justify-between">
              <TabsList>
                <TabsTrigger value="all">
                  All Campaigns ({campaigns.length})
                </TabsTrigger>
                <TabsTrigger value="active">
                  Active ({campaigns.filter(c => c.status === 'active').length})
                </TabsTrigger>
                <TabsTrigger value="draft">
                  Draft ({campaigns.filter(c => c.status === 'draft').length})
                </TabsTrigger>
                <TabsTrigger value="completed">
                  Completed ({campaigns.filter(c => c.status === 'completed').length})
                </TabsTrigger>
              </TabsList>

              {/* Filters */}
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search campaigns..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 w-64"
                  />
                </div>
                
                <Select value={selectedType} onValueChange={(value) => setSelectedType(value as any)}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="awareness">Awareness</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="traffic">Traffic</SelectItem>
                    <SelectItem value="leads">Leads</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  More Filters
                </Button>
              </div>
            </div>

            <TabsContent value="all" className="space-y-4">
              {campaigns.map(campaign => {
                const StatusIcon = STATUS_ICONS[campaign.status]
                
                return (
                  <Card key={campaign.id}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-4">
                          {/* Campaign Header */}
                          <div className="flex items-center gap-3">
                            <StatusIcon className={`h-5 w-5 ${STATUS_COLORS[campaign.status]}`} />
                            <div>
                              <h3 className="text-lg font-semibold">{campaign.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {campaign.description}
                              </p>
                            </div>
                          </div>

                          {/* Campaign Details */}
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">TYPE & STATUS</p>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline">
                                  {campaign.campaign_type}
                                </Badge>
                                <Badge 
                                  variant={campaign.status === 'active' ? 'default' : 'secondary'}
                                >
                                  {campaign.status}
                                </Badge>
                              </div>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">DURATION</p>
                              <p className="text-sm">
                                {format(new Date(campaign.start_date), 'MMM d')} - {' '}
                                {campaign.end_date ? format(new Date(campaign.end_date), 'MMM d, yyyy') : 'Ongoing'}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">BUDGET</p>
                              <p className="text-sm font-medium">
                                ${campaign.budget?.toLocaleString() || '0'}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-1">PLATFORMS</p>
                              <div className="flex items-center gap-1">
                                {campaign.platforms.slice(0, 3).map(platform => (
                                  <div
                                    key={platform}
                                    className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[platform]}`}
                                    title={platform}
                                  />
                                ))}
                                {campaign.platforms.length > 3 && (
                                  <span className="text-xs text-muted-foreground ml-1">
                                    +{campaign.platforms.length - 3}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Goals Progress */}
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">GOALS PROGRESS</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {campaign.goals.slice(0, 3).map((goal, index) => {
                                const progress = getGoalProgress(goal)
                                return (
                                  <div key={index} className="space-y-1">
                                    <div className="flex justify-between text-sm">
                                      <span className="capitalize">{goal.metric}</span>
                                      <span className="font-medium">
                                        {formatMetric(goal.current_value || 0, goal.metric)} / {formatMetric(goal.target_value, goal.metric)}
                                      </span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                      <div
                                        className={`h-2 rounded-full transition-all ${
                                          progress >= 100 ? 'bg-green-500' : 
                                          progress >= 70 ? 'bg-blue-500' : 
                                          progress >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                        }`}
                                        style={{ width: `${progress}%` }}
                                      />
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>

                          {/* Performance Metrics */}
                          {campaign.performance_metrics && (
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-2">PERFORMANCE</p>
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                <div>
                                  <p className="text-muted-foreground">Reach</p>
                                  <p className="font-medium">
                                    {campaign.performance_metrics.total_reach?.toLocaleString() || '0'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Engagement</p>
                                  <p className="font-medium">
                                    {campaign.performance_metrics.total_engagement?.toLocaleString() || '0'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Clicks</p>
                                  <p className="font-medium">
                                    {campaign.performance_metrics.total_clicks?.toLocaleString() || '0'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">Conversion Rate</p>
                                  <p className="font-medium">
                                    {campaign.performance_metrics.conversion_rate?.toFixed(1) || '0'}%
                                  </p>
                                </div>
                                <div>
                                  <p className="text-muted-foreground">ROI</p>
                                  <p className={`font-medium ${
                                    (campaign.performance_metrics.roi || 0) > 0 ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {campaign.performance_metrics.roi || '0'}%
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={() => handleCampaignAction(campaign.id, 'edit')}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleCampaignAction(campaign.id, 'edit')}>
                              Edit Campaign
                            </DropdownMenuItem>
                            {campaign.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleCampaignAction(campaign.id, 'play')}>
                                <Play className="h-4 w-4 mr-2" />
                                Start Campaign
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'active' && (
                              <DropdownMenuItem onClick={() => handleCampaignAction(campaign.id, 'pause')}>
                                <Pause className="h-4 w-4 mr-2" />
                                Pause Campaign
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'paused' && (
                              <DropdownMenuItem onClick={() => handleCampaignAction(campaign.id, 'play')}>
                                <Play className="h-4 w-4 mr-2" />
                                Resume Campaign
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem>
                              <BarChart3 className="h-4 w-4 mr-2" />
                              Analytics Report
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleCampaignAction(campaign.id, 'delete')}
                              className="text-destructive focus:text-destructive"
                            >
                              <Square className="h-4 w-4 mr-2" />
                              Delete Campaign
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </TabsContent>

            {/* Add similar TabsContent for other statuses */}
            <TabsContent value="active">
              {/* Active campaigns would be filtered here */}
            </TabsContent>
            
            <TabsContent value="draft">
              {/* Draft campaigns would be filtered here */}
            </TabsContent>
            
            <TabsContent value="completed">
              {/* Completed campaigns would be filtered here */}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}